<template>
  <div :class="['tabbar', { actions: dropdownActions }]">
    <div class="tabbar-item-container">
      <draggable
        :model-value="tabBarItems"
        class="tabbar-draggable"
        :disabled="!draggable"
        @update:model-value="$emit('update:tabBarItems', $event)"
      >
        <div
          v-for="item in tabBarItems"
          :key="item.emitAction"
          role="link"
          class="tabbar-item"
          :class="[
            item.class,
            {
              active: active == item.emitAction && item.label,
              disabled: disabled && item.emitAction != disabled,
              unclickable: item.unclickable || !item.label,
            },
          ]"
          :action="item.emitAction"
          :data-testid="`tab-item-${item.emitAction}`"
          @click="onTabBarItemClick"
        >
          {{ item.label }}
        </div>
        <div class="tabbar-item filler"></div>
      </draggable>
    </div>

    <div v-if="rowActions" class="tabbar-options tabbar-options-row">
      <va-button
        v-for="action in rowActions"
        :key="action.emitAction"
        :data-testid="`tab-item-${action.emitAction}`"
        variant="text"
        :color="action.type"
        class="tabbar-options-row-button"
        :disabled="action.disabled"
        role="button"
        @click="onDropdownItemClick(action.emitAction)"
      >
        {{ action.label }}
      </va-button>
    </div>

    <div v-if="dropdownActions" class="tabbar-options">
      <va-dropdown
        data-testid="tabbar-dropdown-actions"
        :dropdown-actions="dropdownActions"
        @DROPDOWN_SELECTED="onDropdownItemClick($event)"
      >
        <svg-icon
          icon="options"
          class="icon"
          :title="$translate('broadcast.headings.options')"
        />
      </va-dropdown>
    </div>
  </div>
</template>

<script>
import VaDropdown from '@/components/framework/va-dropdown.vue';
import SvgIcon from '@/components/icons/SvgIcon.vue';
import VaDraggable from '@/components/framework/VaDraggable.vue';
import VaButton from '@/components/framework/VaButton.vue';

export default {
  components: {
    VaDropdown,
    SvgIcon,
    draggable: VaDraggable,
    VaButton,
  },
  props: {
    tabBarItems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    active: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
    },
    dropdownActions: {
      type: Array,
      default: null,
    },
    rowActions: {
      type: Array,
      default: null,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['TABBAR_BUTTON_CLICK', 'TABBAR_ACTION_CLICK', 'update:tabBarItems'],
  methods: {
    onTabBarItemClick(e) {
      if (!this.disabled)
        this.emitItemClick(e.srcElement.getAttribute('action'));
    },
    emitItemClick(item) {
      this.$emit('TABBAR_BUTTON_CLICK', item);
    },
    onDropdownItemClick(item) {
      this.$emit('TABBAR_ACTION_CLICK', item);
    },
  },
};
</script>

<style lang="scss" scoped>
$tab-height: 1.75rem;
$tabbar-height: 3rem;

.tabbar {
  display: flex;
  justify-content: space-between;
  user-select: none;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding-top: 10px;
  margin-bottom: 10px;
  font-family: inherit;
  font-size: 0.875rem;
  text-align: left;
  height: $tabbar-height;

  .tabbar-item-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    box-sizing: border-box;
    padding-bottom: 10px;

    &:hover {
      overflow: scroll hidden;
      padding-bottom: 0;
    }

    .tabbar-draggable {
      position: relative;
      width: 100%;
      display: flex;
      box-sizing: border-box;
      height: $tab-height;
    }

    .tabbar-item {
      box-sizing: content-box;
      color: $color-text-secondary;
      display: inline-block;
      padding: 0 10px;
      padding-bottom: 10px;
      cursor: pointer;
      height: 16px;
      transition: color 0.2s ease-out;
      border-bottom: 2px solid $color-border;
      margin-bottom: -2px;

      // ? line-height is set to deal with overflowing letter Å
      line-height: 20px;

      &.filler {
        pointer-events: none;
        width: 100%;
        padding: 0;
        padding-bottom: 10px;
      }

      &:hover {
        color: $color-text;
      }

      &.active {
        color: $color-text;
        border-bottom: 2px solid $color-fill-active;
      }

      &.disabled {
        display: none;
        opacity: 0;
        cursor: inherit;
      }

      &.unclickable {
        cursor: inherit;
        pointer-events: none;
      }

      &.right {
        float: right;
      }
    }
  }

  .tabbar-options {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    position: relative;
    padding-right: 0.8rem;
    background-color: $color-bg;
    border-bottom: 2px solid $color-border;
    height: $tab-height;

    .icon {
      font-size: 18px;
    }
  }

  .tabbar-options-row {
    display: flex;
  }

  .tabbar-options-row-button {
    margin-left: 1rem;
  }
}
</style>
